import { WarningIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';

import { ICON_WEIGHT } from '@/components/icon';

import { tracks } from '@/lib/db';

import {
  BACKUP_LINK,
  BACKUP_REMINDER_DAYS,
  FIRST_BACKUP_GRACE_DAYS,
  isBackupOverdue,
  useLastBackupAt,
} from './reminder';

/**
 * "A warning banner if no backup has been taken in 30 days" — `PLAN.md` §6
 * Phase 4. Sits on the Flights page: the front door, per `router.tsx`'s own
 * doc comment, and the one place every pilot passes through.
 */
export function BackupReminderBanner() {
  const lastBackupAt = useLastBackupAt();
  /*
   * The oldest import, for the never-backed-up case. "No backup taken yet"
   * used to appear seconds after the first file landed — a warning about
   * data that had existed for less time than it took to read it. Until
   * there has been a backup, the clock starts at the first import instead.
   */
  const firstImportAt = useLiveQuery(() => tracks.firstImportedAt(), [], undefined);

  if (lastBackupAt === undefined || firstImportAt === undefined) {
    return null;
  }
  if (lastBackupAt === null) {
    if (firstImportAt === null || !isBackupOverdue(firstImportAt, FIRST_BACKUP_GRACE_DAYS)) {
      return null;
    }
  } else if (!isBackupOverdue(lastBackupAt)) {
    return null;
  }

  return (
    <p
      className="border-border bg-surface text-muted-foreground rounded-container flex items-start gap-2 border px-4 py-3 text-sm"
      data-testid="backup-reminder"
    >
      <WarningIcon aria-hidden="true" weight={ICON_WEIGHT} className="mt-0.5 size-4 shrink-0" />
      <span>
        {lastBackupAt === null
          ? 'No backup taken yet.'
          : `Over ${String(BACKUP_REMINDER_DAYS)} days since the last backup.`}{' '}
        <Link {...BACKUP_LINK} className="hover:text-foreground underline-offset-2 hover:underline">
          Back up now
        </Link>
        .
      </span>
    </p>
  );
}
